import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import { flushSync } from "react-dom";

/**
 * Управление темой оформления (светлая/тёмная).
 *
 * Как работает:
 *  - тема хранится в localStorage ("ui-theme");
 *  - при первом заходе берётся системная (prefers-color-scheme);
 *  - применяется атрибутом <html data-theme="dark|light"> — CSS-токены делают остальное.
 *
 * Использование:
 *  1) обернуть приложение: <ThemeProvider><App/></ThemeProvider>
 *  2) в любом месте: const { theme, toggleTheme } = useTheme();
 *  3) готовая кнопка: <ThemeToggle /> (src/components/UI/ThemeToggle)
 */

const STORAGE_KEY = "ui-theme";
const ThemeContext = createContext(null);

/** Прочитать стартовую тему: localStorage → системная → light. */
function getInitialTheme() {
  if (typeof window === "undefined") return "light";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch (_) {
    /* localStorage может быть недоступен */
  }
  const prefersDark =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
}

/** Проставить атрибуты темы на <html> (наши токены + тема Bootstrap 5.3). */
function applyTheme(theme) {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", theme);
    // Bootstrap 5.3 читает data-bs-theme → перекрашивает react-bootstrap компоненты.
    document.documentElement.setAttribute("data-bs-theme", theme);
  }
}

// Применяем сразу при импорте модуля — минимизируем «моргание» до маунта провайдера.
applyTheme(getInitialTheme());

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  // useLayoutEffect (не useEffect) — атрибут темы обязан примениться синхронно,
  // до того как View Transition API снимет "финальный" скриншот (см. withTransition).
  useLayoutEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {
      /* ignore */
    }
  }, [theme]);

  // Плавная смена темы: View Transition API кросс-фейдит два скриншота (старый
  // → новый), а не перекрашивает каждый DOM-узел по отдельности — раньше именно
  // поэлементный transition (`* { transition: ... !important }`) лагал на больших
  // таблицах (склад/доставка), потому что анимировался буквально каждый узел.
  const withTransition = useCallback((updater) => {
    if (typeof document === "undefined") {
      setThemeState(updater);
      return;
    }

    if (typeof document.startViewTransition === "function") {
      document.startViewTransition(() => {
        flushSync(() => setThemeState(updater));
      });
      return;
    }

    // Фолбэк для браузеров без View Transition API: одна заливка цветом старой
    // темы, которая гаснет после переключения — дешевле, чем transition на
    // каждом элементе.
    const oldBg = getComputedStyle(document.documentElement)
      .getPropertyValue("--ui-bg")
      .trim();
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:9999;pointer-events:none;" +
      `background:${oldBg};opacity:1;transition:opacity 0.22s ease;`;
    document.body.appendChild(overlay);

    setThemeState(updater);

    requestAnimationFrame(() => {
      overlay.style.opacity = "0";
    });
    window.setTimeout(() => overlay.remove(), 260);
  }, []);

  const setTheme = useCallback(
    (next) => withTransition(() => (next === "dark" ? "dark" : "light")),
    [withTransition],
  );

  const toggleTheme = useCallback(
    () => withTransition((prev) => (prev === "dark" ? "light" : "dark")),
    [withTransition],
  );

  const value = { theme, isDark: theme === "dark", setTheme, toggleTheme };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/** Хук доступа к теме. Работает и без провайдера (fallback), но провайдер рекомендуется. */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;

  // Fallback: провайдер не найден — управляем DOM напрямую, чтобы кнопка не падала.
  const theme =
    (typeof document !== "undefined" &&
      document.documentElement.getAttribute("data-theme")) ||
    "light";
  const setTheme = (next) => applyTheme(next === "dark" ? "dark" : "light");
  return {
    theme,
    isDark: theme === "dark",
    setTheme,
    toggleTheme: () => setTheme(theme === "dark" ? "light" : "dark"),
  };
}

export default ThemeContext;
